import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { feedbackApi } from '../services/feedbackApi';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMessage?: string;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ 
  isOpen, 
  onClose, 
  initialMessage = '' 
}) => {
  const { t, i18n } = useTranslation();
  const [feedbackText, setFeedbackText] = useState(initialMessage);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedbackText.trim()) return;

    setIsSubmitting(true);
    
    try {
      // Submit feedback to backend API
      const result = await feedbackApi.submitFeedback({
        message: feedbackText.trim(),
        language: i18n.language,
        user_agent: navigator.userAgent,
        url: window.location.href
      });
      
      if (result.success) {
        setIsSubmitted(true);
      } else {
        throw new Error(result.message || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      // TODO: Show error message to user in UI
      // For now, we'll still show success to avoid blocking user experience
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFeedbackText('');
    setIsSubmitted(false);
    setIsSubmitting(false);
    onClose();
  };

  const setTemplate = (template: string) => {
    setFeedbackText(template);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header - Matching green theme */}
        <div className="bg-gradient-to-r from-emerald-500 to-green-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">💭</span>
              <div>
                <h3 className="text-lg font-bold">{t('feedback.modalTitle')}</h3>
                <p className="text-orange-100 text-sm">{t('feedback.modalSubtitle')}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:text-orange-200 transition-colors p-1"
              aria-label={t('feedback.close')}
            >
              <span className="text-2xl leading-none">×</span>
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('feedback.messageLabel')}
                </label>
                <textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder={t('feedback.modalPlaceholder')}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors"
                  rows={4}
                  required
                  disabled={isSubmitting}
                />
              </div>
              
              {/* Quick action buttons */}
              <div className="space-y-2">
                <p className="text-xs text-gray-600">{t('feedback.quickActions')}</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setTemplate(t('feedback.templates.general'))}
                    className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded-full transition-colors"
                    disabled={isSubmitting}
                  >
                    👍 {t('feedback.quickButtons.general')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setTemplate(t('feedback.templates.dataSource'))}
                    className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1 rounded-full transition-colors"
                    disabled={isSubmitting}
                  >
                    🌐 {t('feedback.quickButtons.dataSource')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setTemplate(t('feedback.templates.bug'))}
                    className="text-xs bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1 rounded-full transition-colors"
                    disabled={isSubmitting}
                  >
                    🐛 {t('feedback.quickButtons.bug')}
                  </button>
                </div>
              </div>

              <div className="flex space-x-3 pt-2">
                <button
                  type="submit"
                  disabled={!feedbackText.trim() || isSubmitting}
                  className="flex-1 bg-gradient-to-r from-emerald-500 to-green-600 text-white py-3 px-4 rounded-lg font-medium hover:from-emerald-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      {t('feedback.submitting')}
                    </>
                  ) : (
                    t('feedback.submit')
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {t('feedback.cancel')}
                </button>
              </div>
            </form>
          ) : (
            <div className="text-center py-6">
              <div className="text-4xl mb-4">🎉</div>
              <h4 className="text-lg font-semibold text-gray-800 mb-2">
                {t('feedback.thankYou')}
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                {t('feedback.submissionMessage')}
              </p>
              <button
                onClick={handleClose}
                className="bg-emerald-500 text-white px-6 py-2 rounded-lg hover:bg-emerald-600 transition-colors"
              >
                {t('feedback.close')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;
